<template>
  <LanderLayout :config="config">
    <Result v-model:error="error" :is-no-scroll="true" />

    <template v-if="config">
      <div class="form-group">
        <h3>Step 1. Program</h3>
        <LanderProgramSelect :category="config.category" :program-id="formData.programId" @update:program-id="formData.programId = $event" />
      </div>

      <LanderAirport v-model:from="formData.homeAirportFrom" v-model:to="formData.homeAirportTo" v-model:rt-is-diff="formData.homeAirportRtIsDiff" v-model:is-date-diff="formData.homeAirportIsDateDiff" v-model:date-diff="formData.homeAirportDateDiff" :is-diff-dates-shown="config?.options?.is_diff_dates_shown" :current-step="2" traveler-label="" :hide-radios="true" />

      <div id="ideal_flights" class="form-group">
        <h3>Step 3. Flight</h3>

        <div v-if="!searchParamsReady" class="infomsg">Select a program and home airport to view flights.</div>

        <div v-else id="flights">
          <div v-if="!formData.flightsSelected">
            <FlightFilter v-model:filter="flightFilter" v-model:sort="flightSort" :is-lander="true">
              <template #instruction>
                <div class="inline">
                  <template v-if="formData.legIndex === 0">Task 1. Select the outbound flight to the program</template>
                  <template v-else-if="formData.legIndex === 1"><a href="#" class="breadcrumb-link" @click.prevent="goToStep(0)">Task 1</a><span class="icon">&gt;</span>Task 2. Select the return flight home</template>
                </div>
              </template>
            </FlightFilter>

            <FlightList v-model:filter="flightFilter" v-model:sort="flightSort" :flights="flightParams" :leg-index="formData.legIndex" :organization-id="programFull.organization_id" :is-lander="true" :wants-chaperoned="false" :is-dev="false" :program="programFull" :inbound-to="formData.homeAirportRtIsDiff ? formData.homeAirportTo : formData.homeAirportFrom" :cache-bust="searchCacheBust" @update:leg-index="updateLegIndex" @update:is-saved-flights="handleFlightsSaved" @reset-filter-sort="resetFilterSort" @search-results-available="handleSearchResultsAvailable" />
          </div>

          <div v-if="formData.flightsSelected" id="new">
            <div class="ssmsg">
              <a href="#" class="breadcrumb-link" @click.prevent="goToStep(0)">Task 1</a><span class="icon">&gt;</span><a href="#" class="breadcrumb-link" @click.prevent="goToStep(1)">Task 2</a><span class="icon">&gt;</span>Task 3. Select the fare
            </div>
            <FlightSummary :leg-index="formData.legIndex" :flights="flightParams" :program="programFull" :is-lander="true" @update:leg-index="updateLegIndex" @update:is-saved-flights="handleFlightsSaved" />
          </div>
        </div>
      </div>
    </template>
  </LanderLayout>
</template>

<script setup>
import { computed, onMounted, provide, reactive, ref, watch } from "vue";
import { useRoute } from "vue-router";
import useApi from "../utils/apiCalls.js";
import Result from "./common/FormResult.vue";
import LanderLayout from "./LanderLayout.vue";
import LanderProgramSelect from "./LanderProgramSelect.vue";
import LanderAirport from "./LanderAirport.vue";
import FlightFilter from "./TripNewBookFlightsOptionsFlightsFilterSort.vue";
import FlightList from "./TripNewBookFlightsOptionsList.vue";
import FlightSummary from "./TripNewBookFlightsSummaryBook.vue";

const route = useRoute();
const { apiGetRequest } = useApi();

const config = ref(null);
const error = ref(null);
const programFull = ref({});
const searchCacheBust = ref(0);

const formData = reactive({
  programId: null,
  homeAirportFrom: null,
  homeAirportTo: null,
  homeAirportRtIsDiff: false,
  homeAirportIsDateDiff: false,
  homeAirportDateDiff: null,
  flightsSelected: false,
  legIndex: -1,
  searchResultsReady: false,
  flightResultsCount: 0,
});

const flightFilter = ref({
  depart: "",
  name: "",
  policy: false,
  chaperoned: "Undecided",
  stops: "",
});
const flightSort = ref("recommended");

const searchParamsReady = computed(() => {
  if (!formData.programId) return false;
  if (!programFull.value?.id) return false;
  if (!programFull.value.date_start || !programFull.value.date_end) return false;
  if (!formData.homeAirportFrom) return false;
  if (formData.homeAirportRtIsDiff && !formData.homeAirportTo) return false;
  return true;
});

const flightParams = computed(() => {
  if (!searchParamsReady.value) return null;

  return {
    way: "roundtrip",
    route: [
      {
        from: { iata: formData.homeAirportFrom },
        to: { iata: programFull.value.airport_iata_arrive || programFull.value.airport_iata_depart },
        depart: programFull.value.date_start,
        ideal: {},
        flexibility: 2,
      },
      {
        from: { iata: programFull.value.airport_iata_depart || programFull.value.airport_iata_arrive },
        to: { iata: formData.homeAirportRtIsDiff ? formData.homeAirportTo : formData.homeAirportFrom },
        depart: programFull.value.date_end,
        ideal: {},
        flexibility: 2,
      },
    ],
    cabin: "economy",
    pax: 1,
    adults: 1,
    children: 0,
    infants: 0,
  };
});

provide("groupFlightLabel", computed(() => config.value?.label_flight_type_group || "Chaperoned"));
provide("individualFlightLabel", computed(() => config.value?.label_flight_type_individual || "Unchaperoned"));

const loadConfig = async slug => {
  return apiGetRequest({ path: `/landers/${slug}/flights` });
};

const goToStep = step => {
  formData.flightsSelected = false;
  formData.legIndex = step;
  formData.searchResultsReady = false;
  formData.flightResultsCount = 0;
};

const updateLegIndex = value => {
  formData.legIndex = value;
  formData.searchResultsReady = false;
  formData.flightResultsCount = 0;
};

const handleSearchResultsAvailable = count => {
  formData.flightResultsCount = count;
  if (count > 0) {
    formData.searchResultsReady = true;
  }
};

const handleFlightsSaved = value => {
  formData.flightsSelected = value;
  if (!value) {
    formData.searchResultsReady = false;
    formData.flightResultsCount = 0;
  }
};

const resetFilterSort = () => {
  flightFilter.value = {
    depart: "",
    name: "",
    policy: false,
    chaperoned: "Undecided",
    stops: "",
  };
  flightSort.value = "recommended";
};

watch(
  () => formData.programId,
  async newId => {
    programFull.value = {};
    formData.flightsSelected = false;
    formData.searchResultsReady = false;
    formData.flightResultsCount = 0;
    formData.legIndex = -1;
    if (newId) {
      try {
        programFull.value = (await apiGetRequest({ path: `/event/${newId}` })) || {};
      } catch {
        // Silently fail
      }
    }
  }
);

watch(
  () => [formData.homeAirportFrom, formData.homeAirportTo, formData.homeAirportRtIsDiff],
  () => {
    formData.flightsSelected = false;
    formData.searchResultsReady = false;
    formData.flightResultsCount = 0;
    formData.legIndex = searchParamsReady.value ? 0 : -1;
  }
);

watch(
  () => formData.homeAirportRtIsDiff,
  val => {
    if (!val) {
      formData.homeAirportTo = null;
    }
  }
);

watch(
  () => formData.homeAirportIsDateDiff,
  val => {
    if (!val) {
      formData.homeAirportDateDiff = null;
    }
  }
);

watch(
  searchParamsReady,
  ready => {
    formData.legIndex = ready ? 0 : -1;
  },
  { immediate: true }
);

onMounted(async () => {
  try {
    config.value = await loadConfig(route.params.slug);
  } catch (err) {
    error.value = err?.response?.data?.toString() || "You do not have access to this page.";
  }
});
</script>
